// /client/:clientId/piano (finance-pricing target, DD-025): the client's own plan, what we do for them and what it
// costs, and what is in progress. Own lines and agreed prices only; the server reads the client from the token and
// refuses anyone but the client's own contacts. A single column of flat WorkspacePanels (DD-025, DD-002): one per
// contract, its lines as PanelList rows, one for the jobs in progress; the notice, denied and error states are one
// panel's body. One centered spinner while it loads (DD-001).
import React, { useState, type ReactNode } from "react";
import { useOutletContext } from "react-router-dom";
import { SpinnerIcon, WalletIcon } from "@/components/icons";
import { Button } from "@/components/ui/Button";
import { PanelList } from "@/components/ui/PanelList";
import { httpStatusOf } from "@/core/http/HttpError";
import { WorkspacePanel } from "@/domains/Ideas/components/WorkspacePanel";
import { useLocale } from "@/hooks/useLocale";
import { formatMoney } from "@/utils/currency";
import { usePortalPlan } from "../hooks/usePortalPlan";
import { contractSince, contractTitle, lineKind, jobText } from "../utils/portalPlan";

const ROW = "min-h-16 flex flex-col justify-center";
const CURRENT = "current";
// The portal content column every portal page sits in (DD-025).
const COLUMN = "w-full max-w-2xl mx-auto px-4 lg:px-6 py-4";

export const PortalPlanRoute: React.FC = () => {
  const { clientId } = useOutletContext<{ clientId: string }>();
  const { t, locale } = useLocale();
  const query = usePortalPlan(clientId);
  // Each panel minimises on its own, as on every portal panel page.
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const panel = (id: string, title: string, summary: ReactNode, body: ReactNode) => (
    <WorkspacePanel
      key={id}
      title={title}
      icon={<WalletIcon className="w-4 h-4" />}
      summary={summary}
      collapsed={!!collapsed[id]}
      onToggleCollapse={() => setCollapsed((c) => ({ ...c, [id]: !c[id] }))}
    >
      {body}
    </WorkspacePanel>
  );
  if (query.isLoading) {
    return (
      <div className={`${COLUMN} flex justify-center`}>
        <SpinnerIcon className="w-6 h-6 text-gray-400 my-12" />
      </div>
    );
  }
  const plan = query.data;
  if (!plan) {
    const body =
      httpStatusOf(query.error) === 403 ? (
        <p className="text-sm text-gray-600">{t("plan.denied")}</p>
      ) : (
        // A server error or no answer, already retried twice: the client tries again from here, without a reload.
        <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
          <span>{t("plan.error")}</span>
          <Button size="sm" variant="secondary" onClick={() => void query.refetch()} isLoading={query.isFetching}>{t("plan.retry")}</Button>
        </div>
      );
    return <div className={`${COLUMN} space-y-4`}>{panel("notice", t("plan.title"), undefined, body)}</div>;
  }
  return (
    <div className={`${COLUMN} space-y-4`}>
      <div>
        <h1 className="text-2xl font-semibold text-gray-900">{t("plan.title")}</h1>
        <p className="text-sm text-gray-500 mt-1">{t("plan.subtitle")}</p>
      </div>
      {plan.contracts.length === 0
        ? panel("notice", t("plan.title"), undefined, <p className="text-sm text-gray-600">{t("plan.empty")}</p>)
        : plan.contracts.map((contract) =>
            panel(
              contract.id,
              contractTitle(contract, locale),
              contractSince(contract, locale) ?? undefined,
              <PanelList
                items={contract.lines}
                keyFor={(line) => line.id}
                rowClassName={ROW}
                renderItem={(line) => (
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm text-gray-900">{line.label}</div>
                      <div className="text-xs text-gray-500">{lineKind(line, locale)}</div>
                    </div>
                    <div className="text-sm font-semibold tabular-nums whitespace-nowrap">{formatMoney(line.priceMinor, plan.currency)}</div>
                  </div>
                )}
              />,
            ),
          )}
      {plan.current.length > 0 &&
        panel(
          CURRENT,
          t("plan.current"),
          plan.current.length,
          <PanelList
            items={plan.current}
            keyFor={(p) => p.id}
            rowClassName={ROW}
            renderItem={(p) => (
              <div className="flex items-center justify-between gap-3 text-sm">
                <span className="text-gray-700 min-w-0">{jobText(p, locale)}</span>
                <span className="tabular-nums whitespace-nowrap text-gray-900">{formatMoney(p.amountMinor, plan.currency)}</span>
              </div>
            )}
          />,
        )}
      <p className="text-xs text-gray-400">{t("plan.note")}</p>
    </div>
  );
};
